import {
  captureException,
  captureMessage,
  init,
  setContext,
} from '@sentry/browser';
import type { BrowserOptions, User } from '@sentry/browser';

// Rails leaves out nil values, so missing keys are undefined, not null
type SentryConfig = Required<Pick<BrowserOptions, 'dsn' | 'environment'>> &
  Pick<BrowserOptions, 'release'> & { user: User };

const readConfig = (): SentryConfig | null => {
  const element = document.getElementById('sentry-config');
  return element?.textContent ? JSON.parse(element.textContent) : null;
};

const beforeSend: BrowserOptions['beforeSend'] = (event, hint) => {
  // Drop errors from headless Chrome (bots, scrapers, automated tools)
  if (event.request?.headers?.['User-Agent']?.includes('HeadlessChrome')) {
    return null;
  }

  // Read from any thrown value, not only Error instances
  const error = (hint.originalException ?? {}) as {
    message?: unknown;
    name?: unknown;
  };
  const message = typeof error.message === 'string' ? error.message : '';
  const type = typeof error.name === 'string' ? error.name : '';

  // Group errors where a Backbone class is not defined
  if (
    type === 'TypeError' &&
    message.includes('Covidence.') &&
    message.includes('constructor')
  ) {
    event.fingerprint = ['backbone-class-not-defined'];
  }

  // Group errors where the Covidence namespace is not defined
  if (type === 'ReferenceError' && message.includes('Covidence')) {
    event.fingerprint = ['covidence-not-defined'];
  }

  // Group errors where jQuery is not defined
  if (
    type === 'ReferenceError' &&
    (message.includes('$ is not defined') ||
      message.includes("Can't find variable: $"))
  ) {
    event.fingerprint = ['jquery-not-defined'];
  }

  return event;
};

const config = readConfig();

if (config?.dsn) {
  init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    initialScope: {
      user: config.user,
      tags: { heroku_release: config.release, logger: 'javascript' },
    },
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
    allowUrls: [/^https?:\/\/([a-z0-9-]+\.)?covidence\.org/],
    // These errors won't be sent to Sentry and counted in our Quota
    ignoreErrors: [
      // Network errors, such as being offline or leaving the page mid request
      'Failed to fetch',
      'Load failed',
      'The operation was aborted',
      'Network Error',
      'NetworkError',
      'NetworkError when attempting to fetch resource.',
      // Page lifecycle errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    beforeSend,
  });
}

// Always set, with only what callers use so webpack can drop the rest of the SDK
window.Sentry = { captureException, captureMessage, setContext };
